import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import eventBus from '../../core/EventBus';
import FontPicker from '../FontPicker';
import CollagePanel from '../CollagePanel';
import { TEXT_3D_PRESETS } from '../../core/TextEffects3D';
import TemplateWizard from './TemplateWizard';
import LayerPanel from '../Layers/LayerPanel';
import MyDesignsPanel from '../MyDesignsPanel';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).toString();

function __computeApiBase() {
  const loc = window.location;
  const pathParts = loc.pathname.split('/');
  let idx = pathParts.indexOf('selenny-designer');
  if (idx < 0) idx = pathParts.indexOf('designer');
  if (idx >= 0) return loc.origin + pathParts.slice(0, idx + 1).join('/') + '/backend/api';
  return process.env.REACT_APP_API_URL || '/designer/api';
}
const API_BASE = __computeApiBase();

// Cliparts kommen IMMER von der Zentrale (single source of truth).
// Egal welcher Shop den Designer hostet — Cliparts werden zentral gepflegt.
const CENTRAL_API_BASE = 'https://selenny.de/designer/backend/api';

// Shape definitions with inline SVG previews
const SHAPES = [
  { id: 'rectangle', name: 'Rechteck', method: 'addRectangle',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><rect x="4" y="8" width="32" height="24" rx="1"/></svg> },
  { id: 'roundedRect', name: 'Abgerundet', method: 'addRoundedRect',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><rect x="4" y="8" width="32" height="24" rx="8"/></svg> },
  { id: 'circle', name: 'Kreis', method: 'addCircle',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><circle cx="20" cy="20" r="15"/></svg> },
  { id: 'ellipse', name: 'Ellipse', method: 'addEllipse',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><ellipse cx="20" cy="20" rx="17" ry="11"/></svg> },
  { id: 'triangle', name: 'Dreieck', method: 'addTriangle',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><polygon points="20,5 36,35 4,35"/></svg> },
  { id: 'diamond', name: 'Raute', method: 'addDiamond',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><polygon points="20,4 36,20 20,36 4,20"/></svg> },
  { id: 'pentagon', name: 'Fünfeck', method: 'addPolygon',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><polygon points="20,4 37,16 31,35 9,35 3,16"/></svg> },
  { id: 'hexagon', name: 'Hexagon', method: 'addHexagon',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><polygon points="20,4 35,12 35,28 20,36 5,28 5,12"/></svg> },
  { id: 'octagon', name: 'Achteck', method: 'addOctagon',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><polygon points="14,4 26,4 36,14 36,26 26,36 14,36 4,26 4,14"/></svg> },
  { id: 'star', name: 'Stern', method: 'addStar',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><polygon points="20,4 24,15 36,15 27,22 30,34 20,27 10,34 13,22 4,15 16,15"/></svg> },
  { id: 'starBurst12', name: 'Explosion 12', method: 'addStarBurst12',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.85"><polygon points="20,1 24,12 33,3 28,14 39,12 30,21 38,28 27,25 32,38 20,30 14,40 16,28 4,32 11,22 1,23 9,16 3,8 14,13 11,3 18,12"/></svg> },
  { id: 'starBurst16', name: 'Explosion 16', method: 'addStarBurst16',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.85"><polygon points="20,1 23,11 28,3 27,13 36,7 31,15 39,14 33,21 38,27 30,25 35,35 27,30 28,39 21,32 19,40 16,32 9,38 11,28 2,32 8,23 1,20 9,18 3,11 11,13 8,5 15,12 14,2 18,11"/></svg> },
  { id: 'starBurst24', name: 'Explosion 24', method: 'addStarBurst24',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.85"><polygon points="20,1 22,9 25,3 25,11 30,6 28,13 35,9 31,16 38,14 33,20 39,21 33,25 38,30 31,28 35,36 28,30 29,38 24,32 22,40 20,32 17,40 15,32 11,38 12,30 5,36 8,28 2,30 7,25 1,21 7,20 2,14 9,16 5,9 12,13 9,3 14,11 14,3 19,9"/></svg> },
  { id: 'heart', name: 'Herz', method: 'addHeart',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><path d="M20 35 C10 28 2 20 2 13 C2 7 7 3 13 3 C16 3 19 5 20 8 C21 5 24 3 27 3 C33 3 38 7 38 13 C38 20 30 28 20 35Z"/></svg> },
  { id: 'arrow', name: 'Pfeil', method: 'addArrowRight',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><polygon points="4,13 25,13 25,6 38,20 25,34 25,27 4,27"/></svg> },
  { id: 'cross', name: 'Kreuz', method: 'addCross',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><polygon points="15,4 25,4 25,15 36,15 36,25 25,25 25,36 15,36 15,25 4,25 4,15 15,15"/></svg> },
  { id: 'parallelogram', name: 'Parallelogramm', method: 'addParallelogram',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><polygon points="12,8 38,8 28,32 2,32"/></svg> },
  { id: 'trapezoid', name: 'Trapez', method: 'addTrapezoid',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><polygon points="10,8 30,8 38,32 2,32"/></svg> },
  { id: 'ring', name: 'Ring', method: 'addRing',
    icon: <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="5" opacity="0.8"><circle cx="20" cy="20" r="13"/></svg> },
  { id: 'bubble', name: 'Sprechblase', method: 'addBubble',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><path d="M20 4 C10 4 3 10 3 18 C3 26 10 32 18 32 L14 38 L22 32 C30 32 37 26 37 18 C37 10 30 4 20 4Z"/></svg> },
  { id: 'badge', name: 'Badge', method: 'addBadge',
    icon: <svg viewBox="0 0 40 40" fill="currentColor" opacity="0.8"><polygon points="20,3 23,10 28,4 28,12 35,10 31,17 38,20 31,23 35,30 28,28 28,36 23,30 20,37 17,30 12,36 12,28 5,30 9,23 2,20 9,17 5,10 12,12 12,4 17,10"/></svg> },
  { id: 'line', name: 'Linie', method: 'addLine',
    icon: <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="3" opacity="0.8"><line x1="6" y1="34" x2="34" y2="6"/></svg> },
];

const FONTS = [
  'ABeeZee', 'Aclonica', 'Acme', 'Actor', 'Advent Pro', 'Akronim',
  'Alegreya', 'Alegreya Sans', 'Allura', 'Amatic SC', 'Amita', 'Anton',
  'Archivo Black', 'Arial', 'Bad Script', 'Bowlby One SC', 'Bungee Outline',
  'Bungee Shade', 'Comic Sans MS', 'Courier New', 'Dancing Script',
  'Expletus Sans', 'Fredoka One', 'Frijole', 'Georgia', 'Helvetica',
  'Homemade Apple', 'Impact', 'Imprima', 'Inter', 'Kaushan Script',
  'Libre Baskerville', 'Lobster', 'Luckiest Guy', 'Open Sans', 'Oranienbaum',
  'Orbitron', 'Oswald', 'Pacifico', 'Palatino', 'Palanquin', 'Palanquin Dark',
  'Parisienne', 'Paytone One', 'Permanent Marker', 'Pirata One', 'Plaster',
  'Poiret One', 'Prompt', 'Quintessential', 'Qwigley', 'Raleway',
  'Raleway Dots', 'Rammetto One', 'Ranchers', 'Ranga', 'Roboto', 'Rochester',
  'Rock Salt', 'Rubik Mono One', 'Russo One', 'Ruthie', 'Sacramento', 'Sail',
  'Sarina', 'Satisfy', 'Sedgwick Ave', 'Seymour One', 'Shadows Into Light',
  'Shadows Into Light Two', 'Shojumaru', 'Sirin Stencil', 'Sonsie One',
  'Spicy Rice', 'StjediseSans', 'Syncopate', 'Times New Roman', 'Titan One',
  'Trade Winds', 'Trebuchet MS', 'Ubuntu Condensed', 'UnifrakturMaguntia',
  'Varela Round', 'Vast Shadow', 'Verdana', 'Waiting for the Sunrise',
  'Wendy One', 'Wire One', 'Work Sans', 'Yanone Kaffeesatz', 'Yeseva One',
  'Yesteryear', 'Zeyada',
];

const TEXT_PRESETS = [
  { id: 'h1', label: 'Überschrift', preview: 'Überschrift', fontSize: 72, fontWeight: 'bold', desc: '72px, Fett' },
  { id: 'h2', label: '2. Überschrift', preview: 'Zweite Überschrift', fontSize: 48, fontWeight: 'bold', desc: '48px, Fett' },
  { id: 'h3', label: '3. Überschrift', preview: 'Dritte Überschrift', fontSize: 36, fontWeight: '600', desc: '36px, Halbfett' },
  { id: 'body', label: 'Normaler Text', preview: 'Normaler Fließtext', fontSize: 24, fontWeight: 'normal', desc: '24px, Normal' },
  { id: 'small', label: 'Kleiner Text', preview: 'Kleiner Hinweistext', fontSize: 16, fontWeight: 'normal', desc: '16px, Normal' },
  { id: 'footnote', label: 'Fußzeile', preview: 'Fußzeile / Kleingedrucktes', fontSize: 12, fontWeight: '300', desc: '12px, Leicht' },
];

const TEXT_EFFECTS = [
  { id: 'none', label: 'Normal', icon: <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="2"><text x="4" y="18" fontSize="16" fontFamily="sans-serif" fill="currentColor" stroke="none">T</text></svg> },
  { id: 'skew-right', label: 'Neigung rechts', icon: <svg viewBox="0 0 32 24"><text x="6" y="20" fontSize="16" fontFamily="sans-serif" fill="currentColor" transform="skewX(-15) translate(6,0)">T</text></svg> },
  { id: 'skew-left', label: 'Neigung links', icon: <svg viewBox="0 0 32 24"><text x="8" y="20" fontSize="16" fontFamily="sans-serif" fill="currentColor" transform="skewX(15) translate(-2,0)">T</text></svg> },
  { id: 'wide', label: 'Breit dehnen', icon: <svg viewBox="0 0 32 24"><text x="2" y="20" fontSize="16" fontFamily="sans-serif" fill="currentColor" transform="scale(1.4,1) translate(1,0)">T</text></svg> },
  { id: 'tall', label: 'Hoch dehnen', icon: <svg viewBox="0 0 32 24"><text x="9" y="22" fontSize="16" fontFamily="sans-serif" fill="currentColor" transform="scale(1,1.3) translate(0,-2)">T</text></svg> },
  { id: 'squeeze', label: 'Zusammenpressen', icon: <svg viewBox="0 0 32 24"><text x="8" y="20" fontSize="16" fontFamily="sans-serif" fill="currentColor" transform="scale(0.6,1) translate(8,0)">T</text></svg> },
  { id: 'perspective-left', label: 'Perspektive links', icon: <svg viewBox="0 0 32 24"><text x="8" y="18" fontSize="16" fontFamily="sans-serif" fill="currentColor" transform="skewY(8) translate(0,-3)">T</text></svg> },
  { id: 'perspective-right', label: 'Perspektive rechts', icon: <svg viewBox="0 0 32 24"><text x="8" y="18" fontSize="16" fontFamily="sans-serif" fill="currentColor" transform="skewY(-8) translate(0,2)">T</text></svg> },
  { id: 'arc-top', label: 'Bogen oben', icon: <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4,20 Q16,2 28,20"/><circle cx="8" cy="14" r="1.5" fill="currentColor"/><circle cx="16" cy="8" r="1.5" fill="currentColor"/><circle cx="24" cy="14" r="1.5" fill="currentColor"/></svg> },
  { id: 'arc-bottom', label: 'Bogen unten', icon: <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4,6 Q16,24 28,6"/><circle cx="8" cy="12" r="1.5" fill="currentColor"/><circle cx="16" cy="18" r="1.5" fill="currentColor"/><circle cx="24" cy="12" r="1.5" fill="currentColor"/></svg> },
  { id: 'circle', label: 'Kreis', icon: <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="16" cy="12" r="9"/><circle cx="8" cy="6" r="1.5" fill="currentColor"/><circle cx="16" cy="3" r="1.5" fill="currentColor"/><circle cx="24" cy="6" r="1.5" fill="currentColor"/></svg> },
  { id: 'wave', label: 'Welle', icon: <svg viewBox="0 0 32 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2,12 Q10,4 16,12 Q22,20 30,12"/><circle cx="6" cy="8" r="1.5" fill="currentColor"/><circle cx="16" cy="12" r="1.5" fill="currentColor"/><circle cx="26" cy="16" r="1.5" fill="currentColor"/></svg> },
];


const PIXABAY_API_KEY = '33745979-6e793453ab99977fccede77c0';
const PIXABAY_CATEGORIES = [
  'Banner', 'Hintergrund', 'Natur', 'Business', 'Technologie', 'Sport', 'Essen', 'Abstrakt',
];

function LeftToolbar({ canvasManager, imageFilters, activeObject, layerManager, layers, customerEmail, designId, onDesignLoad, allowedClipartCategories = [] }) {
  const [showShapes, setShowShapes] = useState(false);
  const [showCollage, setShowCollage] = useState(false);
  const [showTextDialog, setShowTextDialog] = useState(false);
  const [textTab, setTextTab] = useState('presets'); // 'presets' | 'custom' | 'effects'
  const [textInput, setTextInput] = useState('');
  const [textFont, setTextFont] = useState('Inter');
  const [textSize, setTextSize] = useState(32);
  const [textColor, setTextColor] = useState('#000000');
  const [textWeight, setTextWeight] = useState('normal');
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [bgColor, setBgColor] = useState('#ffffff');
  const [showLayers, setShowLayers] = useState(false);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [showPixabay, setShowPixabay] = useState(false);
  const [showCliparts, setShowCliparts] = useState(false);
  const [showBgImageMenu, setShowBgImageMenu] = useState(false);
  const [hasBgImage, setHasBgImage] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showMyDesigns, setShowMyDesigns] = useState(false);
  const [bgImageTab, setBgImageTab] = useState('upload'); // 'upload' | 'pixabay'
  const [bgPixabayQuery, setBgPixabayQuery] = useState('');
  const [bgPixabayResults, setBgPixabayResults] = useState([]);
  const [bgPixabayLoading, setBgPixabayLoading] = useState(false);
  const [bgPixabayPage, setBgPixabayPage] = useState(1);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [bgPixabayTotal, setBgPixabayTotal] = useState(0);
  const [clipartCategories, setClipartCategories] = useState([]);
  const [clipartActiveCategory, setClipartActiveCategory] = useState('alle');
  const [clipartLoading, setClipartLoading] = useState(false);
  const [clipartUploading, setClipartUploading] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [pixabayQuery, setPixabayQuery] = useState('');
  const [pixabayResults, setPixabayResults] = useState([]);
  const [pixabayPage, setPixabayPage] = useState(1);
  const [pixabayTotal, setPixabayTotal] = useState(0);
  const [pixabayLoading, setPixabayLoading] = useState(false);
  const [pixabayActiveCategory, setPixabayActiveCategory] = useState('');
  const [showUploadDisclaimer, setShowUploadDisclaimer] = useState(false);
  const [showPrintFileConfirm, setShowPrintFileConfirm] = useState(false);
  const fileInputRef = useRef(null);
  const bgImageInputRef = useRef(null);
  const printFileInputRef = useRef(null);
  const textInputRef = useRef(null);

  const closeAllDialogs = useCallback(() => {
    setShowShapes(false);
    setShowCollage(false);
    setShowTextDialog(false);
    setShowQRDialog(false);
    setShowBgColorPicker(false);
    setShowBgImageMenu(false);
    setShowImageMenu(false);
    setShowPixabay(false);
    setShowCliparts(false);
    setShowLayers(false);
    setShowTemplates(false);
    setShowMyDesigns(false);
    setShowPrintFileConfirm(false);
  }, []);

  useEffect(() => {
    if (showTextDialog && textTab === 'custom' && textInputRef.current) {
      setTimeout(() => textInputRef.current.focus(), 50);
    }
  }, [showTextDialog, textTab]);

  // Close dialogs when clicking on the canvas stage or canvas element
  useEffect(() => {
    const unsub1 = eventBus.on('stage:click', closeAllDialogs);
    const unsub2 = eventBus.on('toolbar:close-dialogs', closeAllDialogs);
    return () => { unsub1(); unsub2(); };
  }, [closeAllDialogs]);

  // Track background image state
  useEffect(() => {
    if (canvasManager) setHasBgImage(!!canvasManager.hasBackgroundImage?.());
    const unsub1 = eventBus.on('bgimage:changed', (has) => setHasBgImage(has));
    const unsub2 = eventBus.on('bgcolor:changed', (color) => setBgColor(color));
    return () => { unsub1(); unsub2(); };
  }, [canvasManager]);

  // Listen for assistant panel-open requests
  useEffect(() => {
    const unsub = eventBus.on('assistant:open-panel', (data) => {
      if (!data || !data.panel) return;
      closeAllDialogs();
      switch (data.panel) {
        case 'shapes': setShowShapes(true); break;
        case 'text': setShowTextDialog(true); break;
        case 'collage': setShowCollage(true); break;
        case 'qr': setShowQRDialog(true); break;
        case 'bgcolor': setShowBgColorPicker(true); break;
        case 'bgimage': setShowBgImageMenu(true); break;
        case 'pixabay': setShowPixabay(true); break;
        case 'cliparts': setShowCliparts(true); break;
        case 'layers': setShowLayers(true); break;
        case 'templates': setShowTemplates(true); break;
        case 'mydesigns': setShowMyDesigns(true); break;
        default: break;
      }
    });
    return () => unsub();
  }, [closeAllDialogs]);

  const pixabaySearch = useCallback(async (query, page = 1, append = false) => {
    setPixabayLoading(true);
    try {
      const q = encodeURIComponent(query || 'design');
      const url = `https://pixabay.com/api/?key=${PIXABAY_API_KEY}&q=${q}&image_type=photo&per_page=21&page=${page}&lang=de`;
      const res = await fetch(url);
      const data = await res.json();
      setPixabayResults(prev => append ? [...prev, ...data.hits] : data.hits);
      setPixabayTotal(data.totalHits || 0);
      setPixabayPage(page);
    } catch (err) {
      console.error('Pixabay-Fehler:', err);
    } finally {
      setPixabayLoading(false);
    }
  }, []);

  const bgPixabaySearch = useCallback(async (query, page = 1, append = false) => {
    setBgPixabayLoading(true);
    try {
      const q = encodeURIComponent(query || 'hintergrund');
      const url = `https://pixabay.com/api/?key=${PIXABAY_API_KEY}&q=${q}&image_type=photo&per_page=21&page=${page}&lang=de`;
      const res = await fetch(url);
      const data = await res.json();
      setBgPixabayResults(prev => append ? [...prev, ...data.hits] : data.hits);
      setBgPixabayTotal(data.totalHits || 0);
      setBgPixabayPage(page);
    } catch (err) {
      console.error('Pixabay-Fehler:', err);
    } finally {
      setBgPixabayLoading(false);
    }
  }, []);

  const handleOpenPixabay = useCallback(() => {
    closeAllDialogs();
    setShowPixabay(true);
    setPixabayQuery('');
    setPixabayActiveCategory('');
    setPixabayResults([]);
    pixabaySearch('design', 1);
  }, [closeAllDialogs, pixabaySearch]);

  const handlePixabayAddImage = useCallback((hit) => {
    if (!canvasManager) return;
    const maxW = Math.min(500, canvasManager.canvas.getWidth() * 0.6);
    canvasManager.addImage(hit.largeImageURL, { maxWidth: maxW, crossOrigin: 'anonymous' })
      .catch(() => alert('Bild konnte nicht geladen werden'));
  }, [canvasManager]);

  const loadCliparts = useCallback(async () => {
    setClipartLoading(true);
    try {
      const res = await fetch(`${CENTRAL_API_BASE}/cliparts`);
      const data = await res.json();
      setClipartCategories(data.categories || []);
    } catch (err) {
      console.error('Cliparts laden fehlgeschlagen:', err);
    } finally {
      setClipartLoading(false);
    }
  }, []);

  // Pro Produkt erlaubte Clipart-Kategorien (leer = alle). Filtert Anzeige -> weniger Laden.
  const isClipartCatAllowed = useCallback(
    (id) => !allowedClipartCategories || allowedClipartCategories.length === 0 || allowedClipartCategories.includes(id),
    [allowedClipartCategories]
  );

  const handleAddClipart = useCallback((clip) => {
    if (!canvasManager) return;
    // clip.url ist relativ (z. B. /designer/backend/api/cliparts/file/abi/xxx.png).
    // Mit CENTRAL_API_BASE wird daraus eine absolute URL auf die Zentrale.
    const url = clip.url.startsWith('http') ? clip.url : 'https://selenny.de' + clip.url;
    const isSvg = clip.filename.toLowerCase().endsWith('.svg');
    if (isSvg) {
      canvasManager.addSVG(url, { name: clip.name }).catch(() => {});
    } else {
      canvasManager.addImage(url, { maxWidth: 200, name: clip.name, crossOrigin: 'anonymous' }).catch(() => {});
    }
  }, [canvasManager]);

  const handleClipartUpload = useCallback(async (categoryId, files) => {
    if (!files || files.length === 0) return;
    setClipartUploading(true);
    try {
      const formData = new FormData();
      formData.append('category', categoryId);
      for (let i = 0; i < files.length; i++) {
        formData.append('files[]', files[i]);
      }
      const res = await fetch(`${CENTRAL_API_BASE}/cliparts/upload`, { method: 'POST', body: formData, credentials: 'omit' });
      const data = await res.json();
      if (data.errors && data.errors.length > 0) {
        console.warn('Upload-Fehler:', data.errors);
      }
      loadCliparts();
    } catch (err) {
      console.error('Upload fehlgeschlagen:', err);
    } finally {
      setClipartUploading(false);
    }
  }, [loadCliparts]);

  const handleCreateCategory = useCallback(async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/cliparts/category`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategoryName.trim() }),
      });
      await res.json();
      setNewCategoryName('');
      setShowNewCategoryInput(false);
      loadCliparts();
    } catch (err) {
      console.error('Kategorie erstellen fehlgeschlagen:', err);
    }
  }, [newCategoryName, loadCliparts]);

  const handleDeleteClipart = useCallback(async (categoryId, filename) => {
    try {
      await fetch(`${API_BASE}/cliparts/${encodeURIComponent(categoryId)}/${encodeURIComponent(filename)}`, { method: 'DELETE' });
      loadCliparts();
    } catch (err) {
      console.error('Löschen fehlgeschlagen:', err);
    }
  }, [loadCliparts]);

  if (!canvasManager) return null;

  const handleAddShape = (method) => {
    if (typeof canvasManager[method] === 'function') {
      canvasManager[method]();
    }
    setShowShapes(false);
  };

  const handleOpenTextDialog = () => {
    closeAllDialogs();
    setTextInput('');
    setTextTab('presets');
    setShowTextDialog(true);
  };

  // Scale factor for proportional text sizing on any canvas
  const scale = canvasManager.getObjScale ? canvasManager.getObjScale() : 1;

  // Add text via preset
  const handleAddPreset = (preset) => {
    const text = textInput.trim() || preset.preview;
    canvasManager.addText(text, {
      fontSize: Math.round(preset.fontSize * scale),
      fontFamily: textFont,
      fontWeight: preset.fontWeight,
      fill: textColor,
    });
  };

  // Add text via custom settings
  const handleAddText = () => {
    const text = textInput.trim() || 'Text';
    canvasManager.addText(text, {
      fontSize: Math.round(textSize * scale),
      fontFamily: textFont,
      fontWeight: textWeight,
      fill: textColor,
    });
  };

  // Apply text effect to selected object
  const handleApplyEffect = (effectId) => {
    canvasManager.applyTextEffect(effectId);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // PDF handling
    if (file.type === 'application/pdf') {
      setUploadLoading(true);
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(event.target.result) }).promise;
          const page = await pdf.getPage(1);
          // Dynamischer Scale: max 4000px auf der längeren Seite um Browser-Crashes zu vermeiden
          const baseViewport = page.getViewport({ scale: 1 });
          const maxDim = Math.max(baseViewport.width, baseViewport.height);
          const scale = maxDim > 1333 ? Math.min(3, 4000 / maxDim) : 3;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          await page.render({ canvasContext: ctx, viewport }).promise;
          const dataUrl = canvas.toDataURL('image/png');
          const maxW = Math.min(400, canvasManager.canvas.getWidth() * 0.5);
          await canvasManager.addImage(dataUrl, { maxWidth: maxW, name: file.name });
        } catch (err) {
          console.error('PDF render error:', err);
          alert('PDF konnte nicht geladen werden. Bitte versuche eine kleinere Datei.');
        } finally {
          setUploadLoading(false);
        }
      };
      reader.onerror = () => { setUploadLoading(false); alert('Datei konnte nicht gelesen werden'); };
      reader.readAsArrayBuffer(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (!file.type.startsWith('image/')) { alert('Nur Bild- oder PDF-Dateien erlaubt'); return; }
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const maxW = Math.min(400, canvasManager.canvas.getWidth() * 0.5);
        canvasManager.addImage(event.target.result, { maxWidth: maxW, name: file.name })
          .catch(() => alert('Bild konnte nicht geladen werden'));
      };
      img.onerror = () => alert('Bild konnte nicht gelesen werden');
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBgImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Nur Bilddateien erlaubt'); return; }
    const reader = new FileReader();
    reader.onload = (event) => {
      canvasManager.setBackgroundImage(event.target.result);
    };
    reader.readAsDataURL(file);
    if (bgImageInputRef.current) bgImageInputRef.current.value = '';
  };

  const handlePrintFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/webp'];
    if (!allowedTypes.includes(file.type) && !file.type.startsWith('image/')) {
      alert('Nur PDF- oder Bilddateien erlaubt (PDF, JPG, PNG, TIFF)');
      return;
    }

    setUploadLoading(true);

    // Store original file as base64 for later download
    const origReader = new FileReader();
    origReader.onload = (origEvent) => {
      const originalBase64 = origEvent.target.result;
      canvasManager._printFileOriginal = originalBase64;
      canvasManager._printFileName = file.name;
      canvasManager._printFileType = file.type;
      eventBus.emit('printfile:changed', { name: file.name });
    };
    origReader.readAsDataURL(file);

    if (file.type === 'application/pdf') {
      // PDF: render page 1 at high resolution, set as background
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(event.target.result) }).promise;
          const page = await pdf.getPage(1);
          const baseViewport = page.getViewport({ scale: 1 });
          const maxDim = Math.max(baseViewport.width, baseViewport.height);
          const scale = maxDim > 1333 ? Math.min(4, 6000 / maxDim) : 4;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          await page.render({ canvasContext: ctx, viewport }).promise;
          const dataUrl = canvas.toDataURL('image/png');
          canvasManager.setBackgroundImage(dataUrl);
        } catch (err) {
          console.error('PDF render error:', err);
          alert('PDF konnte nicht geladen werden.');
        } finally {
          setUploadLoading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      // Image: set directly as background
      const reader = new FileReader();
      reader.onload = (event) => {
        canvasManager.setBackgroundImage(event.target.result);
        setUploadLoading(false);
      };
      reader.readAsDataURL(file);
    }
    if (printFileInputRef.current) printFileInputRef.current.value = '';
  };

  const handleAddQR = () => {
    if (!qrUrl.trim()) return;
    canvasManager.addQRCode(qrUrl.trim());
    setShowQRDialog(false);
    setQrUrl('');
  };

  const handleBgColor = (color) => {
    setBgColor(color);
    canvasManager.setBackgroundColor(color);
  };

  const toolBtn = (onClick, tooltip, icon, isActive = false, label = null) => (
    <button
      onClick={onClick}
      data-tooltip={tooltip}
      data-tooltip-pos="right"
      className={`flex flex-col items-center justify-center gap-0.5 w-full px-1 py-2 rounded-lg transition-all duration-150 ${isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`}
    >
      <div className="w-5 h-5">{icon}</div>
      {label && <span className="text-[9px] font-medium leading-tight text-center whitespace-pre-line">{label}</span>}
    </button>
  );

  // Compute a preview color that's always readable on the panel background
  const isDark = document.documentElement.classList.contains('dark');
  const previewColor = (() => {
    const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(textColor);
    if (!m) return textColor;
    const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    if (isDark) return lum < 0.15 ? '#ffffff' : textColor;
    return lum > 0.85 ? '#000000' : textColor;
  })();

  const isTextSelected = activeObject && ['i-text', 'text', 'textbox'].includes(activeObject.type);

  return (
    <div className="bg-gray-100/80 border-r border-gray-200 flex flex-col items-center py-2 gap-0.5 shrink-0 relative z-20" style={{ overflow: 'visible', width: 76 }}>

      {/* Vorschläge & Anlässe — prominent top position */}
      <div data-guide-id="tool-templates">
      {toolBtn(
        () => { closeAllDialogs(); setShowTemplates(!showTemplates); }, 'Vorschläge',
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>,
        showTemplates, 'Vorschläge'
      )}
      </div>

      {/* Meine Designs */}
      <div data-guide-id="tool-mydesigns">
      {toolBtn(
        () => { closeAllDialogs(); setShowMyDesigns(!showMyDesigns); }, 'Meine Designs',
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>,
        showMyDesigns, 'Meine\nDesigns'
      )}
      </div>

      <div className="w-14 h-px bg-gray-300/50 my-0.5" />

      {/* Shapes */}
      <div data-guide-id="tool-shapes">
      {toolBtn(
        () => { closeAllDialogs(); setShowShapes(!showShapes); }, 'Formen',
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="8" height="8" rx="1"/><circle cx="17" cy="17" r="4"/><polygon points="17,3 21,11 13,11"/></svg>,
        showShapes, 'Flächen &\nLinien'
      )}
      </div>

      {/* Text */}
      <div data-guide-id="tool-text">
      {toolBtn(
        handleOpenTextDialog, 'Text hinzufügen',
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7V4h16v3M9 20h6M12 4v16"/></svg>,
        showTextDialog, 'Text'
      )}
      </div>

      {/* Image Insert (own + Pixabay) */}
      <div data-guide-id="tool-image">
      {toolBtn(
        () => { closeAllDialogs(); setShowImageMenu(!showImageMenu); }, 'Bild einfügen',
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>,
        showImageMenu || showPixabay, 'Bilder'
      )}
      </div>
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml,application/pdf" onChange={handleImageUpload} style={{ display: 'none' }} />
      <input ref={printFileInputRef} type="file" accept="application/pdf,image/jpeg,image/png,image/tiff,image/webp" onChange={handlePrintFileUpload} style={{ display: 'none' }} />

      {/* Upload Loading Overlay */}
      {uploadLoading && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16
        }}>
          <div style={{
            width: 56, height: 56,
            border: '3px solid rgba(255,255,255,0.2)',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'selenny-upload-spin 0.8s linear infinite'
          }} />
          <span style={{ color: '#fff', fontSize: 15, fontWeight: 500 }}>PDF wird verarbeitet...</span>
          <style>{`@keyframes selenny-upload-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Cliparts */}
      <div data-guide-id="tool-cliparts">
      {toolBtn(
        () => { closeAllDialogs(); if (!showCliparts) { loadCliparts(); } setShowCliparts(!showCliparts); }, 'Cliparts',
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14l-5-4.87 6.91-1.01z"/></svg>,
        showCliparts, 'Cliparts'
      )}
      </div>

      {/* Fotocollage / Layouts */}
      <div data-guide-id="tool-collage">
      {toolBtn(
        () => { closeAllDialogs(); setShowCollage(!showCollage); }, 'Fotocollage',
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="8" height="8" rx="1"/><rect x="13" y="3" width="8" height="8" rx="1"/><rect x="3" y="13" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/></svg>,
        showCollage, 'Collagen\nLayouts'
      )}
      </div>

      <div className="w-14 h-px bg-gray-300/50 my-0.5" />

      {/* QR Code */}
      <div data-guide-id="tool-qr">
      {toolBtn(
        () => { closeAllDialogs(); setShowQRDialog(!showQRDialog); }, 'QR-Code',
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/><rect x="18" y="18" width="3" height="3"/></svg>,
        showQRDialog, 'QR-Code'
      )}
      </div>

      {/* Background Color */}
      <div data-guide-id="tool-bgcolor">
      {toolBtn(
        () => { closeAllDialogs(); setShowBgColorPicker(!showBgColorPicker); }, 'Hintergrundfarbe',
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M12 17v-6M9 14l3-3 3 3" strokeLinecap="round" strokeLinejoin="round"/></svg>,
        showBgColorPicker, 'Hintergrund-\nfarbe'
      )}
      </div>

      {/* Background Image */}
      <div data-guide-id="tool-bgimage">
      <input ref={bgImageInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleBgImageUpload} style={{ display: 'none' }} />
      {toolBtn(
        () => { closeAllDialogs(); setShowBgImageMenu(!showBgImageMenu); }, 'Hintergrundbild',
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 15l5-5 4 4 3-3 6 6"/><circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/></svg>,
        showBgImageMenu || hasBgImage, 'Hintergrund-\nbild'
      )}
      </div>

      {/* Layers */}
      <div data-guide-id="tool-layers">
      {toolBtn(
        () => { closeAllDialogs(); setShowLayers(!showLayers); }, 'Ebenen',
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
        showLayers, 'Ebenen'
      )}
      </div>

      {/* Spacer to push bottom tools down */}
      <div className="flex-1" />

      {/* Selenny® Branding — 3D App Icon */}
      <style>{`
        @keyframes selennyShine { 0% { left: -80%; } 25% { left: 130%; } 100% { left: 130%; } }
        @keyframes selennyGlow { 0% { opacity: 0.4; transform: scale(1); } 100% { opacity: 0.8; transform: scale(1.15); } }
      `}</style>
      <a
        href="https://selenny.de"
        target="_blank"
        rel="noopener noreferrer"
        className="flex flex-col items-center gap-1 no-underline opacity-70 hover:opacity-100 transition-all hover:scale-105 py-2 mb-14"
        style={{ marginLeft: 4 }}
        title="Powered by Selenny®"
      >
        <div style={{ position: 'relative' }}>
          {/* Glow */}
          <div style={{
            position: 'absolute', inset: -8, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(59,130,246,0.3) 0%, rgba(59,130,246,0.08) 50%, transparent 70%)',
            filter: 'blur(10px)', animation: 'selennyGlow 3s ease-in-out infinite alternate',
          }} />
          {/* Chrome ring */}
          <div style={{
            position: 'relative', width: 56, height: 56, borderRadius: 16, padding: 2,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.25), rgba(255,255,255,0.05), rgba(255,255,255,0.12))',
            boxShadow: '0 6px 28px rgba(0,0,0,0.15), 0 0 50px rgba(59,130,246,0.12)',
          }}>
            {/* Glass face */}
            <div style={{
              width: '100%', height: '100%', borderRadius: 14, overflow: 'hidden',
              background: 'linear-gradient(135deg, #4b8af0, #2563eb, #1e40af)',
              boxShadow: 'inset 0 2px 3px rgba(255,255,255,0.15), inset 0 -2px 4px rgba(0,0,0,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
            }}>
              {/* Highlight */}
              <div style={{
                position: 'absolute', top: 0, left: 2, right: 2, height: '45%',
                borderRadius: '12px 12px 0 0',
                background: 'linear-gradient(to bottom, rgba(255,255,255,0.22), rgba(255,255,255,0.06), transparent)',
                pointerEvents: 'none',
              }} />
              {/* S */}
              <span style={{
                color: '#fff', fontWeight: 800, fontSize: 26, position: 'relative', zIndex: 1,
                textShadow: '0 2px 8px rgba(0,0,0,0.3), 0 0 20px rgba(255,255,255,0.1)',
                fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
              }}>S</span>
              {/* Shine sweep */}
              <div style={{
                position: 'absolute', top: '-50%', left: '-80%', width: '60%', height: '200%',
                background: 'linear-gradient(100deg, transparent 20%, rgba(255,255,255,0.25) 50%, transparent 80%)',
                transform: 'skewX(-20deg)', animation: 'selennyShine 4s 1s infinite',
                pointerEvents: 'none',
              }} />
            </div>
          </div>
        </div>
        <span style={{ fontSize: '8px', fontWeight: 700, color: '#94a3b8', fontFamily: '-apple-system, sans-serif', letterSpacing: '0.3px' }}>Selenny®</span>
      </a>

      <div className="pb-2" />

      {/* === POPUPS === */}

      {/* Meine Designs Panel */}
      {showMyDesigns && (
        <MyDesignsPanel
          customerEmail={customerEmail}
          canvasManager={canvasManager}
          designId={designId}
          onDesignLoad={onDesignLoad}
          onClose={() => setShowMyDesigns(false)}
        />
      )}

      {/* Template Wizard */}
      {showTemplates && (
        <TemplateWizard
          canvasManager={canvasManager}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {/* Collage Panel */}
      {showCollage && (
        <CollagePanel
          canvasManager={canvasManager}
          onClose={() => setShowCollage(false)}
        />
      )}

      {/* Shapes Grid Panel */}
      {showShapes && (
        <div className="fixed z-[100] bg-white dark:bg-[#2c2c2e] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-3 animate-slideInLeft"
          style={{ left: 84, top: 100, width: 300 }}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Formen</p>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowShapes(false)} className="text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-100 px-2 py-0.5 rounded">Schliessen</button>
              <button onClick={() => setShowShapes(false)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full text-lg leading-none">&times;</button>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {SHAPES.map((shape) => (
              <button
                key={shape.id}
                onClick={() => handleAddShape(shape.method)}
                className="w-12 h-12 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 flex items-center justify-center text-gray-600 hover:text-blue-600 transition-all group relative"
                data-tooltip={shape.name}
              >
                <div className="w-8 h-8">{shape.icon}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Text Dialog - Wide Panel */}
      {showTextDialog && (
        <div className="fixed z-[100] bg-white dark:bg-[#2c2c2e] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-slideInLeft flex flex-col"
          style={{ left: 84, top: 80, width: 420, maxHeight: 'calc(100vh - 120px)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Text hinzufügen</p>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowTextDialog(false)} className="text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-0.5 rounded">Schliessen</button>
              <button onClick={() => setShowTextDialog(false)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-lg leading-none">&times;</button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-600 px-4 gap-0">
            {[
              { key: 'presets', label: 'Textvorlagen' },
              { key: 'custom', label: 'Eigener Text' },
              { key: 'effects', label: 'Effekte' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setTextTab(tab.key)}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                  textTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-4 overflow-y-auto flex-1" style={{ maxHeight: 'calc(100vh - 300px)' }}>

            {/* ===== PRESETS TAB ===== */}
            {textTab === 'presets' && (
              <div>
                {/* Optional text input for presets */}
                <div className="mb-3">
                  <label className="text-xs text-gray-500 mb-1 block">Eigenen Text eingeben (optional)</label>
                  <input
                    type="text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="Oder eigenen Text eingeben..."
                    className="selenny-input text-sm"
                  />
                </div>

                {/* Font + Color row */}
                <div className="flex gap-2 mb-4">
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 mb-1 block">Schriftart</label>
                    <FontPicker value={textFont} onChange={(f) => setTextFont(f)} />
                  </div>
                  <div className="w-14">
                    <label className="text-xs text-gray-500 mb-1 block">Farbe</label>
                    <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="w-full h-[34px] cursor-pointer rounded border border-gray-300" />
                  </div>
                </div>

                {/* Preset buttons */}
                <div className="space-y-1.5">
                  {TEXT_PRESETS.map((preset) => {
                    const scaledSize = Math.round(preset.fontSize * scale);
                    return (
                      <button
                        key={preset.id}
                        onClick={() => handleAddPreset(preset)}
                        className="w-full text-left px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-all group"
                      >
                        <div className="flex items-baseline justify-between">
                          <span
                            className="group-hover:text-blue-700 dark:group-hover:text-blue-400 truncate"
                            style={{
                              fontFamily: textFont,
                              fontSize: Math.min(preset.fontSize * 0.45, 28),
                              fontWeight: preset.fontWeight,
                              lineHeight: 1.3,
                              color: previewColor,
                            }}
                          >
                            {textInput.trim() || preset.preview}
                          </span>
                          <span className="text-[10px] text-gray-400 ml-2 whitespace-nowrap flex-shrink-0">{scaledSize}px</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ===== CUSTOM TAB ===== */}
            {textTab === 'custom' && (
              <div>
                <div className="mb-3">
                  <label className="text-xs text-gray-500 mb-1 block">Text eingeben</label>
                  <textarea
                    ref={textInputRef}
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="Ihr Text hier..."
                    className="selenny-input text-sm"
                    rows={3}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddText(); }}
                  />
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-500 mb-1 block">Schriftart</label>
                  <select value={textFont} onChange={(e) => setTextFont(e.target.value)} className="selenny-input text-xs">
                    {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                  </select>
                </div>
                <div className="flex gap-2 mb-3">
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 mb-1 block">Größe (px)</label>
                    <input type="number" value={textSize} onChange={(e) => setTextSize(Number(e.target.value))} className="selenny-input text-xs" min="8" max="500" />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 mb-1 block">Schriftstil</label>
                    <select value={textWeight} onChange={(e) => setTextWeight(e.target.value)} className="selenny-input text-xs">
                      <option value="300">Leicht</option>
                      <option value="normal">Normal</option>
                      <option value="600">Halbfett</option>
                      <option value="bold">Fett</option>
                      <option value="900">Extra Fett</option>
                    </select>
                  </div>
                  <div className="w-14">
                    <label className="text-xs text-gray-500 mb-1 block">Farbe</label>
                    <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="w-full h-[34px] cursor-pointer rounded border border-gray-300" />
                  </div>
                </div>

                {/* Preview */}
                <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[48px] flex items-center justify-center overflow-hidden">
                  <span
                    className="break-words text-center"
                    style={{
                      fontFamily: textFont,
                      fontSize: Math.min(textSize, 48),
                      fontWeight: textWeight,
                      color: previewColor,
                      lineHeight: 1.2,
                    }}
                  >
                    {textInput || 'Vorschau'}
                  </span>
                </div>

              </div>
            )}

            {/* ===== EFFECTS TAB ===== */}
            {textTab === 'effects' && (
              <div>
                {!isTextSelected ? (
                  <div className="text-center py-8">
                    <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M4 7V4h16v3M9 20h6M12 4v16"/>
                    </svg>
                    <p className="text-sm text-gray-500">Wählen Sie zuerst ein Textobjekt auf der Bühne aus</p>
                    <p className="text-xs text-gray-400 mt-1">Klicken Sie auf einen Text, um Effekte anzuwenden</p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-gray-500 mb-3">Effekt auf ausgewaehlten Text anwenden:</p>
                    <div className="grid grid-cols-4 gap-2">
                      {TEXT_EFFECTS.map((effect) => (
                        <button
                          key={effect.id}
                          onClick={() => handleApplyEffect(effect.id)}
                          className="flex flex-col items-center gap-1 p-2 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all text-gray-600 hover:text-blue-600"
                          data-tooltip={effect.label}
                        >
                          <div className="w-8 h-6">{effect.icon}</div>
                          <span className="text-[9px] text-gray-500 leading-tight text-center">{effect.label}</span>
                        </button>
                      ))}
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-2">Manuelle Einstellungen:</p>
                      <div className="space-y-2">
                        <div>
                          <label className="text-[11px] text-gray-500 flex justify-between">
                            <span>Neigung X (Skew)</span>
                            <span className="font-mono">{activeObject?.skewX?.toFixed(0) || 0}&deg;</span>
                          </label>
                          <input
                            type="range" min="-45" max="45" step="1"
                            value={activeObject?.skewX || 0}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              activeObject.set('skewX', v);
                              activeObject.setCoords();
                              canvasManager.canvas.renderAll();
                            }}
                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] text-gray-500 flex justify-between">
                            <span>Neigung Y</span>
                            <span className="font-mono">{activeObject?.skewY?.toFixed(0) || 0}&deg;</span>
                          </label>
                          <input
                            type="range" min="-45" max="45" step="1"
                            value={activeObject?.skewY || 0}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              activeObject.set('skewY', v);
                              activeObject.setCoords();
                              canvasManager.canvas.renderAll();
                            }}
                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] text-gray-500 flex justify-between">
                            <span>Breite (ScaleX)</span>
                            <span className="font-mono">{((activeObject?.scaleX || 1) * 100).toFixed(0)}%</span>
                          </label>
                          <input
                            type="range" min="20" max="300" step="5"
                            value={(activeObject?.scaleX || 1) * 100}
                            onChange={(e) => {
                              const v = Number(e.target.value) / 100;
                              activeObject.set('scaleX', v);
                              activeObject.setCoords();
                              canvasManager.canvas.renderAll();
                            }}
                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] text-gray-500 flex justify-between">
                            <span>Hoehe (ScaleY)</span>
                            <span className="font-mono">{((activeObject?.scaleY || 1) * 100).toFixed(0)}%</span>
                          </label>
                          <input
                            type="range" min="20" max="300" step="5"
                            value={(activeObject?.scaleY || 1) * 100}
                            onChange={(e) => {
                              const v = Number(e.target.value) / 100;
                              activeObject.set('scaleY', v);
                              activeObject.setCoords();
                              canvasManager.canvas.renderAll();
                            }}
                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => handleApplyEffect('none')}
                        className="selenny-btn-secondary w-full text-xs mt-3"
                      >
                        Alle Effekte zurücksetzen
                      </button>
                    </div>

                    {/* 3D Text-Effekte */}
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-2">3D Text-Stile</p>
                      <p className="text-[10px] text-gray-400 mb-3">Neuen Text mit 3D-Effekt einfügen:</p>
                      <div className="grid grid-cols-3 gap-1.5">
                        {TEXT_3D_PRESETS.map((preset) => (
                          <button
                            key={preset.id}
                            onClick={() => canvasManager?.add3DText(preset.id, activeObject?.text || 'Dein Text', 72)}
                            className="rounded-lg border border-gray-200 hover:border-blue-400 transition-all overflow-hidden"
                            style={{ background: preset.preview.bg, color: preset.preview.color, padding: '8px 4px', fontSize: '10px', fontWeight: 700, lineHeight: 1.1, textAlign: 'center' }}
                          >
                            <span style={{ display: 'block', fontSize: '14px', marginBottom: '2px' }}>Aa</span>
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

          </div>

          {/* Sticky footer for Text Dialog */}
          {textTab === 'custom' && (
            <div className="border-t border-gray-200 bg-white px-4 py-3 shrink-0">
              <button onClick={handleAddText} className="selenny-btn-primary w-full text-sm flex items-center justify-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
                Text einfügen
              </button>
              <p className="text-[10px] text-gray-400 mt-1 text-center">Cmd+Enter zum schnellen Einfügen</p>
            </div>
          )}
        </div>
      )}

      {/* QR Dialog */}
      {showQRDialog && (
        <div className="fixed z-[100] bg-white dark:bg-[#2c2c2e] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 w-72 animate-slideInLeft"
          style={{ left: 84, top: 220 }}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">QR-Code erstellen</p>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowQRDialog(false)} className="text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-100 px-2 py-0.5 rounded">Schliessen</button>
              <button onClick={() => setShowQRDialog(false)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full text-lg leading-none">&times;</button>
            </div>
          </div>
          <div className="mb-3">
            <label className="text-xs text-gray-600 mb-1 block">URL / Text</label>
            <input type="url" value={qrUrl} onChange={(e) => setQrUrl(e.target.value)} placeholder="https://example.com" className="selenny-input text-sm" autoFocus onKeyDown={(e) => e.key === 'Enter' && handleAddQR()} />
          </div>
          <button onClick={handleAddQR} className="selenny-btn-primary w-full text-sm">QR-Code einfügen</button>
        </div>
      )}

      {/* BG Image Panel */}
      {showBgImageMenu && (
        <div className="fixed z-[100] bg-white dark:bg-[#2c2c2e] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-slideInLeft"
          style={{ left: 84, top: 280, width: 380, maxHeight: 'calc(100vh - 320px)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Hintergrundbild</p>
            <button onClick={() => setShowBgImageMenu(false)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full text-lg leading-none">&times;</button>
          </div>

          {/* Tabs */}
          <div className="flex px-4 pb-2 gap-1">
            <button
              onClick={() => setBgImageTab('upload')}
              className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                bgImageTab === 'upload' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Eigene Datei
            </button>
            <button
              onClick={() => { setBgImageTab('cliparts'); if (clipartCategories.length === 0) loadCliparts(); }}
              className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                bgImageTab === 'cliparts' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Cliparts
            </button>
            <button
              onClick={() => { setBgImageTab('pixabay'); if (bgPixabayResults.length === 0) bgPixabaySearch('hintergrund', 1); }}
              className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                bgImageTab === 'pixabay' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Pixabay
            </button>
          </div>

          {/* Cliparts Tab — Bilder aus Clipart-Bibliothek als Hintergrund setzen */}
          {bgImageTab === 'cliparts' && (
            <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 420px)' }}>
              {/* Kategorien-Filter */}
              {clipartCategories.length > 0 && (
                <div className="px-4 pb-2 flex flex-wrap gap-1">
                  <button
                    onClick={() => setClipartActiveCategory('alle')}
                    className={`px-2 py-1 text-[11px] rounded-md transition-colors ${
                      clipartActiveCategory === 'alle' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Alle
                  </button>
                  {clipartCategories.filter(c => isClipartCatAllowed(c.id)).map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setClipartActiveCategory(cat.id)}
                      className={`px-2 py-1 text-[11px] rounded-md transition-colors ${
                        clipartActiveCategory === cat.id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
              {/* Clipart-Grid */}
              <div className="px-4 pb-3 overflow-y-auto" style={{ flex: 1 }}>
                {clipartLoading ? (
                  <div className="text-center py-6 text-xs text-gray-500">Cliparts werden geladen…</div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {clipartCategories
                      .filter(c => clipartActiveCategory === 'alle' || c.id === clipartActiveCategory)
                      .filter(c => isClipartCatAllowed(c.id))
                      .flatMap(c => (c.items || []).map(it => ({ ...it, _catName: c.name })))
                      .map((item) => (
                        <button
                          key={item.id || item.url}
                          onClick={async () => {
                            // Setze Clipart als Hintergrundbild
                            try {
                              await canvasManager?.setBackgroundImage?.(item.url);
                              setHasBgImage(true);
                              setShowBgImageMenu(false);
                            } catch (e) {
                              console.error('Hintergrund setzen fehlgeschlagen:', e);
                            }
                          }}
                          className="aspect-square bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-lg overflow-hidden flex items-center justify-center transition-colors"
                          title={item.name || item._catName}
                        >
                          <img
                            src={item.thumbnail || item.url}
                            alt={item.name || ''}
                            className="w-full h-full object-contain p-1"
                            loading="lazy"
                          />
                        </button>
                      ))}
                  </div>
                )}
                {!clipartLoading && clipartCategories.length === 0 && (
                  <div className="text-center py-6 text-xs text-gray-500">Keine Cliparts verfügbar</div>
                )}
              </div>
              {hasBgImage && (
                <div className="px-4 pb-3">
                  <button
                    onClick={() => {
                      canvasManager?.removeBackgroundImage?.();
                      setHasBgImage(false);
                    }}
                    className="w-full py-2 px-3 rounded-lg text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                  >
                    Hintergrundbild entfernen
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Upload Tab */}
          {bgImageTab === 'upload' && (
            <div className="px-4 pb-4 flex flex-col gap-2">
              <button
                onClick={() => bgImageInputRef.current?.click()}
                className="w-full py-2.5 px-3 rounded-lg text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
                Bild hochladen
              </button>
              {hasBgImage && (
                <button
                  onClick={() => {
                    canvasManager?.removeBackgroundImage?.();
                    setHasBgImage(false);
                  }}
                  className="w-full py-2 px-3 rounded-lg text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/></svg>
                  Hintergrundbild entfernen
                </button>
              )}
            </div>
          )}

          {/* Pixabay Tab */}
          {bgImageTab === 'pixabay' && (
            <div className="flex flex-col">
              {/* Search bar */}
              <div className="px-4 pb-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={bgPixabayQuery}
                    onChange={(e) => setBgPixabayQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') bgPixabaySearch(bgPixabayQuery || 'hintergrund', 1);
                    }}
                    placeholder="Hintergrundbilder suchen..."
                    className="selenny-input text-sm flex-1"
                    autoFocus
                  />
                  <button
                    onClick={() => bgPixabaySearch(bgPixabayQuery || 'hintergrund', 1)}
                    className="selenny-btn-primary text-sm px-3"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                  </button>
                </div>
              </div>

              {/* Category chips */}
              <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                {PIXABAY_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setBgPixabayQuery(cat);
                      bgPixabaySearch(cat.toLowerCase(), 1);
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      bgPixabayQuery === cat
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Results */}
              <div className="px-4 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 520px)' }}>
                {bgPixabayLoading && bgPixabayResults.length === 0 && (
                  <div className="text-center py-8">
                    <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <p className="text-xs text-gray-500 mt-2">Bilder werden geladen...</p>
                  </div>
                )}

                {!bgPixabayLoading && bgPixabayResults.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">Keine Bilder gefunden</p>
                    <p className="text-xs text-gray-400 mt-1">Versuchen Sie einen anderen Suchbegriff</p>
                  </div>
                )}

                {bgPixabayResults.length > 0 && (
                  <>
                    <p className="text-[10px] text-gray-400 mb-2">{bgPixabayTotal.toLocaleString('de-DE')} Ergebnisse</p>
                    <div className="grid grid-cols-3 gap-2">
                      {bgPixabayResults.map((hit) => (
                        <button
                          key={hit.id}
                          onClick={() => {
                            if (canvasManager?.setBackgroundImage) {
                              canvasManager.setBackgroundImage(hit.largeImageURL);
                            }
                          }}
                          className="group rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all text-left"
                        >
                          <div className="relative aspect-[4/3] bg-gray-100">
                            <img
                              src={hit.webformatURL}
                              alt={hit.tags}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                              <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 15l5-5 4 4 3-3 6 6"/></svg>
                            </div>
                          </div>
                          <div className="px-1.5 py-1">
                            <p className="text-[9px] text-gray-500 truncate">{hit.webformatWidth}&times;{hit.webformatHeight}</p>
                          </div>
                        </button>
                      ))}
                    </div>

                    {bgPixabayResults.length < bgPixabayTotal && (
                      <button
                        onClick={() => bgPixabaySearch(bgPixabayQuery || 'hintergrund', bgPixabayPage + 1, true)}
                        disabled={bgPixabayLoading}
                        className="selenny-btn-secondary w-full text-xs mt-3"
                      >
                        {bgPixabayLoading ? 'Laden...' : 'Mehr laden'}
                      </button>
                    )}

                    <p className="text-[9px] text-gray-300 text-center mt-2">Bilder bereitgestellt von Pixabay</p>
                  </>
                )}
              </div>

              {/* Remove bg image button at bottom of Pixabay tab too */}
              {hasBgImage && (
                <div className="px-4 pb-3 border-t border-gray-100 pt-2">
                  <button
                    onClick={() => {
                      canvasManager?.removeBackgroundImage?.();
                      setHasBgImage(false);
                    }}
                    className="w-full py-2 px-3 rounded-lg text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/></svg>
                    Hintergrundbild entfernen
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* BG Color Picker */}
      {showBgColorPicker && (
        <div className="fixed z-[100] bg-white dark:bg-[#2c2c2e] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 animate-slideInLeft"
          style={{ left: 84, top: 280, width: 240 }}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Hintergrundfarbe</p>
            <button onClick={() => setShowBgColorPicker(false)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full text-lg leading-none">&times;</button>
          </div>

          {/* Current color preview */}
          <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
            <div className="w-8 h-8 rounded-lg border-2 border-gray-300 shrink-0" style={{ backgroundColor: bgColor }} />
            <span className="text-xs text-gray-500 font-mono">{bgColor}</span>
          </div>

          {/* Color picker button - clearly labeled */}
          <label className="flex items-center gap-2 w-full py-2.5 px-3 rounded-lg text-sm font-medium bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors cursor-pointer mb-3">
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 2a10 10 0 0110 10" stroke="#ef4444" />
              <path d="M22 12a10 10 0 01-10 10" stroke="#22c55e" />
              <path d="M12 22A10 10 0 012 12" stroke="#3b82f6" />
              <path d="M2 12A10 10 0 0112 2" stroke="#f59e0b" />
            </svg>
            <span>Eigene Farbe mischen</span>
            <input type="color" value={bgColor} onChange={(e) => handleBgColor(e.target.value)} className="w-0 h-0 opacity-0 absolute" />
          </label>

          {/* Preset colors with label */}
          <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide mb-2">Schnellauswahl</p>
          <div className="grid grid-cols-6 gap-1.5">
            {[
              '#ffffff', '#f3f4f6', '#e5e7eb', '#d1d5db', '#6b7280', '#000000',
              '#fecaca', '#ef4444', '#dc2626', '#fde68a', '#f59e0b', '#d97706',
              '#bbf7d0', '#22c55e', '#16a34a', '#bfdbfe', '#3b82f6', '#1d4ed8',
              '#e9d5ff', '#8b5cf6', '#7c3aed', '#fce7f3', '#ec4899', '#db2777',
            ].map((c) => (
              <button key={c} onClick={() => handleBgColor(c)}
                className={`w-8 h-8 rounded-lg border-2 cursor-pointer hover:scale-110 transition-transform ${bgColor === c ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'}`}
                style={{ backgroundColor: c }} title={c}
              />
            ))}
          </div>

          {/* Reset button */}
          <button
            onClick={() => handleBgColor('#ffffff')}
            className="w-full mt-3 py-1.5 px-3 rounded-lg text-xs text-gray-500 hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Zurücksetzen (Weiß)
          </button>
        </div>
      )}

      {/* Cliparts Panel */}
      {showCliparts && (
        <div className="fixed z-[100] bg-white dark:bg-[#2c2c2e] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-slideInLeft"
          style={{ left: 84, top: 100, width: 380, maxHeight: 'calc(100vh - 140px)' }}
        >
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Cliparts</p>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowCliparts(false)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full text-lg leading-none">&times;</button>
            </div>
          </div>

          {/* Category tabs */}
          <div className="flex flex-wrap gap-1 px-3 pb-2">
            <button
              onClick={() => setClipartActiveCategory('alle')}
              className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${
                clipartActiveCategory === 'alle' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >Alle</button>
            {clipartCategories.filter((cat) => isClipartCatAllowed(cat.id)).map((cat) => (
              <button
                key={cat.id}
                onClick={() => setClipartActiveCategory(cat.id)}
                className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${
                  clipartActiveCategory === cat.id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.label} {cat.count > 0 && <span className="text-[9px] opacity-70">({cat.count})</span>}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="px-3 pb-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
            {clipartLoading && (
              <div className="text-center py-8">
                <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-xs text-gray-500 mt-2">Cliparts laden...</p>
              </div>
            )}

            {!clipartLoading && clipartCategories.length === 0 && (
              <div className="text-center py-8">
                <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14l-5-4.87 6.91-1.01z"/></svg>
                <p className="text-xs text-gray-500">Noch keine Cliparts vorhanden</p>
                <p className="text-[10px] text-gray-400 mt-1">Erstelle eine Kategorie und lade Bilder hoch</p>
              </div>
            )}

            {!clipartLoading && clipartCategories
              .filter((cat) => isClipartCatAllowed(cat.id))
              .filter((cat) => clipartActiveCategory === 'alle' || cat.id === clipartActiveCategory)
              .map((cat) => (
                <div key={cat.id} className="mb-4">
                  <p className="text-xs font-semibold text-gray-700 mb-2">{cat.label}</p>
                  {cat.files.length === 0 ? (
                    <div className="text-center py-4 border border-dashed border-gray-200 rounded-lg">
                      <p className="text-[10px] text-gray-400">Keine Cliparts vorhanden</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {cat.files.map((clip) => (
                        <button
                          key={clip.filename}
                          onClick={() => handleAddClipart(clip)}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('application/selenny-clipart', JSON.stringify({
                              url: clip.url,
                              filename: clip.filename,
                              name: clip.name,
                            }));
                            e.dataTransfer.effectAllowed = 'copy';
                          }}
                          className="w-full aspect-square rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 flex items-center justify-center p-1.5 transition-all overflow-hidden cursor-grab active:cursor-grabbing"
                          title={clip.name}
                        >
                          {/* Galerie zeigt das kleine Thumbnail (thumbUrl). Platziert/gedruckt
                              wird immer das Original (clip.url) — volle Druckqualitaet. */}
                          <img
                            src={(() => { const s = clip.thumbUrl || clip.url; return s.startsWith('http') ? s : 'https://selenny.de' + s; })()}
                            alt={clip.name}
                            className="w-full h-full object-contain pointer-events-none"
                            loading="lazy"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))
            }
          </div>
        </div>
      )}

      {/* Image Source Menu */}
      {showImageMenu && !showPixabay && (
        <div className="fixed z-[100] bg-white dark:bg-[#2c2c2e] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-2 animate-slideInLeft"
          style={{ left: 84, top: 140, width: 260 }}
        >
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 pt-1 pb-2">Bild einfügen</p>
          <button
            onClick={() => {
              setShowImageMenu(false);
              if (sessionStorage.getItem('selenny-upload-accepted')) {
                fileInputRef.current?.click();
              } else {
                setShowUploadDisclaimer(true);
              }
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-blue-50 transition-colors text-left group"
          >
            <div className="w-9 h-9 rounded-lg bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center text-gray-500 group-hover:text-blue-600 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800 group-hover:text-blue-700">Eigenes Bild</p>
              <p className="text-[11px] text-gray-400">JPG, PNG, SVG vom Computer</p>
            </div>
            <svg className="w-4 h-4 text-gray-300 group-hover:text-blue-400 ml-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <div style={{ height: 1, background: 'var(--border-light, #e5e7eb)', margin: '4px 8px' }} />
          <button
            onClick={() => {
              setShowImageMenu(false);
              setShowPrintFileConfirm(true);
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-green-50 transition-colors text-left group"
          >
            <div className="w-9 h-9 rounded-lg bg-green-50 group-hover:bg-green-100 flex items-center justify-center text-green-600 group-hover:text-green-700 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M12 18v-6"/><path d="M9 15l3-3 3 3"/></svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800 group-hover:text-green-700">Eigene Druckdatei</p>
              <p className="text-[11px] text-gray-400">PDF oder Bild als Druckvorlage</p>
            </div>
            <svg className="w-4 h-4 text-gray-300 group-hover:text-green-500 ml-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <div style={{ height: 1, background: 'var(--border-light, #e5e7eb)', margin: '4px 8px' }} />
          <button
            onClick={() => { setShowImageMenu(false); handleOpenPixabay(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-blue-50 transition-colors text-left group"
          >
            <div className="w-9 h-9 rounded-lg bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center text-gray-500 group-hover:text-blue-600 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><circle cx="8" cy="10" r="2" fill="currentColor"/><path d="M22 16l-5-5-3 3-2-2-8 8"/></svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800 group-hover:text-blue-700">Lizenzfreies Bild</p>
              <p className="text-[11px] text-gray-400">Kostenlose Bilder von Pixabay</p>
            </div>
            <svg className="w-4 h-4 text-gray-300 group-hover:text-blue-400 ml-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <div style={{ height: 1, background: 'var(--border-light, #e5e7eb)', margin: '4px 8px' }} />
          <button
            onClick={() => {
              setShowImageMenu(false);
              if (!showCliparts) { loadCliparts(); }
              setShowCliparts(true);
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-purple-50 transition-colors text-left group"
          >
            <div className="w-9 h-9 rounded-lg bg-purple-50 group-hover:bg-purple-100 flex items-center justify-center text-purple-600 group-hover:text-purple-700 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800 group-hover:text-purple-700">Cliparts &amp; Mediathek</p>
              <p className="text-[11px] text-gray-400">Vektorgrafiken aus der Zentrale</p>
            </div>
            <svg className="w-4 h-4 text-gray-300 group-hover:text-purple-400 ml-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      )}

      {showPixabay && (
        <div className="fixed z-[100] bg-white dark:bg-[#2c2c2e] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-slideInLeft"
          style={{ left: 84, top: 160, width: 420, maxHeight: 'calc(100vh - 200px)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Pixabay Bildersuche</p>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowPixabay(false)} className="text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-100 px-2 py-0.5 rounded">Schliessen</button>
              <button onClick={() => setShowPixabay(false)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full text-lg leading-none">&times;</button>
            </div>
          </div>

          {/* Search bar */}
          <div className="px-4 pb-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={pixabayQuery}
                onChange={(e) => setPixabayQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPixabayActiveCategory('');
                    pixabaySearch(pixabayQuery || 'design', 1);
                  }
                }}
                placeholder="Bilder suchen..."
                className="selenny-input text-sm flex-1"
                autoFocus
              />
              <button
                onClick={() => { setPixabayActiveCategory(''); pixabaySearch(pixabayQuery || 'design', 1); }}
                className="selenny-btn-primary text-sm px-3"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
              </button>
            </div>
          </div>

          {/* Category chips */}
          <div className="px-4 pb-2 flex flex-wrap gap-1.5">
            {PIXABAY_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  const q = cat.toLowerCase();
                  setPixabayQuery(cat);
                  setPixabayActiveCategory(cat);
                  pixabaySearch(q, 1);
                }}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  pixabayActiveCategory === cat
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Results */}
          <div className="px-4 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 380px)' }}>
            {pixabayLoading && pixabayResults.length === 0 && (
              <div className="text-center py-8">
                <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-xs text-gray-500 mt-2">Bilder werden geladen...</p>
              </div>
            )}

            {!pixabayLoading && pixabayResults.length === 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Keine Bilder gefunden</p>
                <p className="text-xs text-gray-400 mt-1">Versuchen Sie einen anderen Suchbegriff</p>
              </div>
            )}

            {pixabayResults.length > 0 && (
              <>
                <p className="text-[10px] text-gray-400 mb-2">{pixabayTotal.toLocaleString('de-DE')} Ergebnisse</p>
                <div className="grid grid-cols-3 gap-2">
                  {pixabayResults.map((hit) => (
                    <button
                      key={hit.id}
                      onClick={() => handlePixabayAddImage(hit)}
                      className="group rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all text-left"
                    >
                      <div className="relative aspect-[4/3] bg-gray-100">
                        <img
                          src={hit.webformatURL}
                          alt={hit.tags}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                        </div>
                      </div>
                      <div className="px-1.5 py-1">
                        <p className="text-[9px] text-gray-500 truncate">{hit.webformatWidth}&times;{hit.webformatHeight}</p>
                        <p className="text-[9px] text-gray-400 truncate">{hit.user}</p>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Load more */}
                {pixabayResults.length < pixabayTotal && (
                  <button
                    onClick={() => pixabaySearch(pixabayQuery || 'design', pixabayPage + 1, true)}
                    disabled={pixabayLoading}
                    className="selenny-btn-secondary w-full text-xs mt-3"
                  >
                    {pixabayLoading ? 'Laden...' : 'Mehr laden'}
                  </button>
                )}

                <p className="text-[9px] text-gray-300 text-center mt-2">Bilder bereitgestellt von Pixabay</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Layers Panel */}
      {showLayers && (
        <div className="fixed z-[100] bg-white dark:bg-[#2c2c2e] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-slideInLeft"
          style={{ left: 84, top: 100, width: 280, maxHeight: 'calc(100vh - 140px)' }}
        >
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Ebenen</p>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowLayers(false)} className="text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-0.5 rounded">Schliessen</button>
              <button onClick={() => setShowLayers(false)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-lg leading-none">&times;</button>
            </div>
          </div>
          <p className="px-4 pb-1 text-[10px] text-gray-400 dark:text-gray-500">Ziehen zum Verschieben, Doppelklick zum Umbenennen</p>
          <div className="px-2 pb-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
            <LayerPanel
              layers={layers}
              layerManager={layerManager}
              activeObject={activeObject}
            />
          </div>
        </div>
      )}

      {/* Upload Disclaimer Modal */}
      {/* Print File Size Confirmation Dialog */}
      {showPrintFileConfirm && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center"
          onClick={() => setShowPrintFileConfirm(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative bg-white dark:bg-[#1c1c1e] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 mx-4"
            style={{ maxWidth: 480, width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <svg className="w-7 h-7 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="12" y2="12" />
                  <line x1="15" y1="15" x2="12" y2="12" />
                </svg>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-3">
              Eigene Druckdatei hochladen
            </h3>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">
                    Wichtig: Bannergr&ouml;&szlig;e pr&uuml;fen!
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    Haben Sie bereits die gew&uuml;nschte und richtige Bannergr&ouml;&szlig;e eingestellt? Ihre Druckdatei wird auf die gesamte Fl&auml;che gestreckt.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Ihre Datei wird als Hintergrund auf die aktuell eingestellte Gr&ouml;&szlig;e
              (<strong>{canvasManager?.widthCm || '–'} &times; {canvasManager?.heightCm || '–'} cm</strong>) platziert.
              Veredelungen wie &Ouml;sen und Saum werden dar&uuml;ber angezeigt, damit Sie die Platzierung pr&uuml;fen k&ouml;nnen.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
              Unterst&uuml;tzte Formate: PDF, JPG, PNG, TIFF
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPrintFileConfirm(false)}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  setShowPrintFileConfirm(false);
                  setTimeout(() => printFileInputRef.current?.click(), 100);
                }}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium bg-green-500 text-white hover:bg-green-600 active:bg-green-700 transition-colors shadow-sm"
              >
                Ja, Gr&ouml;&szlig;e stimmt &mdash; Datei hochladen
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadDisclaimer && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center"
          onClick={() => setShowUploadDisclaimer(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Card */}
          <div
            className="relative bg-white dark:bg-[#1c1c1e] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 mx-4"
            style={{ maxWidth: 480, width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <svg className="w-7 h-7 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-3">
              Hinweis zum Bild-Upload
            </h3>

            {/* Body */}
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Mit dem Hochladen eines Bildes best&auml;tigen Sie:
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 mb-4">
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 flex-shrink-0">&bull;</span>
                <span>Sie besitzen die vollst&auml;ndigen Bildrechte oder haben die ausdr&uuml;ckliche Genehmigung des Rechteinhabers.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 flex-shrink-0">&bull;</span>
                <span>Das Bild enth&auml;lt keine anst&ouml;&szlig;igen, diskriminierenden, gewaltverherrlichenden oder menschenverachtenden Inhalte.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 flex-shrink-0">&bull;</span>
                <span>Sie tragen die alleinige Verantwortung f&uuml;r den hochgeladenen Inhalt — sowohl in rechtlicher als auch in qualitativer Hinsicht.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 flex-shrink-0">&bull;</span>
                <span>Selenny&reg; &uuml;bernimmt keine Haftung f&uuml;r Inhalte, die von Nutzern hochgeladen werden.</span>
              </li>
            </ul>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
              Bei Verst&ouml;&szlig;en gegen diese Richtlinien behalten wir uns vor, Auftr&auml;ge abzulehnen.
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowUploadDisclaimer(false)}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  sessionStorage.setItem('selenny-upload-accepted', '1');
                  setShowUploadDisclaimer(false);
                  setTimeout(() => fileInputRef.current?.click(), 100);
                }}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700 transition-colors shadow-sm"
              >
                Verstanden & Akzeptieren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeftToolbar;
